import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
  TrendingUp, TrendingDown, Plus, Trash2, Loader2, Wallet, DollarSign, Truck, Target,
} from "lucide-react";
import { api, formatCOP } from "@/lib/api";
import { toast } from "sonner";

const shortCOP = (n) => {
  const v = Math.round(Number(n) || 0);
  if (Math.abs(v) >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `$${Math.round(v / 1000)}k`;
  return `$${v}`;
};

const trend = (serie) => {
  if (!serie || serie.length < 2) return null;
  const prev = serie[serie.length - 2];
  const last = serie[serie.length - 1];
  const diff = last.revenue - prev.revenue;
  const pct = prev.revenue > 0 ? (diff / prev.revenue) * 100 : (last.revenue > 0 ? 100 : 0);
  return { up: diff >= 0, pct: Math.abs(pct), prev: prev.period, last: last.period };
};

function Chart({ data, title, testid }) {
  const t = trend(data);
  return (
    <div className="bg-white rounded-2xl border border-[var(--lc-border)] p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-display font-bold text-[var(--lc-ink)]">{title}</h4>
        {t && (
          <span className={`flex items-center gap-1 text-sm font-bold ${t.up ? "text-[var(--lc-success)]" : "text-[var(--lc-accent)]"}`}>
            {t.up ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {t.pct.toFixed(0)}% {t.up ? "más" : "menos"} vs {t.prev}
          </span>
        )}
      </div>
      {data.length === 0 ? (
        <p className="text-sm text-[var(--lc-slate)] py-10 text-center">Aún no hay datos de ventas pagadas.</p>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={shortCOP} tick={{ fontSize: 11 }} width={48} />
            <Tooltip formatter={(v) => formatCOP(v)} />
            <Legend />
            <Bar dataKey="revenue" name="Ventas" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
            <Bar dataKey="profit" name="Ganancia" fill="#22c55e" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default function FinancePanel() {
  const [data, setData] = useState(null);
  const [concept, setConcept] = useState("");
  const [amount, setAmount] = useState("");
  const [adding, setAdding] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const load = async (s = start, e = end) => {
    const params = {};
    if (s) params.start = s;
    if (e) params.end = e;
    const { data } = await api.get("/admin/finance", { params });
    setData(data);
  };

  useEffect(() => { load(); }, []);

  const clearRange = () => {
    setStart(""); setEnd("");
    load("", "");
  };

  const addInvestment = async (e) => {
    e.preventDefault();
    if (!concept.trim() || !amount) return;
    setAdding(true);
    try {
      await api.post("/admin/finance/investments", { concept: concept.trim(), amount: Number(amount) });
      setConcept(""); setAmount("");
      await load();
      toast.success("Inversión registrada");
    } catch {
      toast.error("No se pudo registrar");
    } finally {
      setAdding(false);
    }
  };

  const removeInvestment = async (id) => {
    try {
      await api.delete(`/admin/finance/investments/${id}`);
      await load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  if (!data) {
    return <div className="py-20 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[var(--lc-primary)]" /></div>;
  }

  const s = data.summary;

  const cards = [
    { label: "Total vendido", value: s.total_sold, icon: DollarSign, color: "#0ea5e9", testid: "fin-total-sold" },
    { label: "Costo de libros", value: s.total_cost, icon: Wallet, color: "#f59e0b", testid: "fin-total-cost" },
    { label: "Ingreso por envíos", value: s.shipping_income, icon: Truck, color: "#8b5cf6", testid: "fin-shipping-income" },
    { label: "Ganancia neta", value: s.net_profit, icon: TrendingUp, color: "#22c55e", testid: "fin-net-profit" },
  ];

  return (
    <div className="space-y-6" data-testid="finance-panel">
      {/* Date range filter */}
      <div className="bg-white rounded-2xl border border-[var(--lc-border)] p-4 flex flex-wrap items-end gap-3" data-testid="finance-date-filter">
        <div>
          <label className="text-xs font-bold text-[var(--lc-slate)] mb-1 block">Desde</label>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            data-testid="finance-start-date"
            className="rounded-xl border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] outline-none px-3 py-2 bg-white"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-[var(--lc-slate)] mb-1 block">Hasta</label>
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            data-testid="finance-end-date"
            className="rounded-xl border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] outline-none px-3 py-2 bg-white"
          />
        </div>
        <button
          onClick={() => load()}
          data-testid="finance-apply-range"
          className="rounded-full bg-[var(--lc-primary)] text-white px-5 py-2.5 font-display font-extrabold hover:bg-[var(--lc-primary-hover)] transition-colors"
        >
          Aplicar
        </button>
        {(start || end) && (
          <button
            onClick={clearRange}
            data-testid="finance-clear-range"
            className="rounded-full border-2 border-[var(--lc-border)] text-[var(--lc-slate)] px-5 py-2 font-semibold hover:border-[var(--lc-primary)] transition-colors"
          >
            Limpiar
          </button>
        )}
        <span className="text-sm text-[var(--lc-slate)] ml-auto self-center">
          {start || end ? `Mostrando ${start || "inicio"} → ${end || "hoy"}` : "Mostrando todo el histórico"}
        </span>
      </div>

      {/* Rentabilidad */}
      <div
        className={`rounded-2xl p-6 text-white ${s.is_profit ? "bg-[var(--lc-success)]" : "bg-[var(--lc-accent)]"}`}
        data-testid="fin-profitability"
      >
        <div className="flex items-center gap-3">
          {s.is_profit ? <TrendingUp className="w-8 h-8" /> : <TrendingDown className="w-8 h-8" />}
          <div>
            <p className="font-display font-black text-2xl" data-testid="fin-profit-status">
              {s.is_profit ? "Negocio en GANANCIA" : "Aún recuperando la inversión"}
            </p>
            <p className="opacity-90 text-sm">
              Balance vs capital invertido:{" "}
              <strong data-testid="fin-balance">{formatCOP(s.balance_vs_capital)}</strong>
              {"  ·  "}ROI: <strong data-testid="fin-roi">{s.roi_percent.toFixed(1)}%</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl border border-[var(--lc-border)] p-4" data-testid={c.testid}>
            <div className="flex items-center gap-2 text-[var(--lc-slate)] text-sm font-semibold">
              <c.icon className="w-4 h-4" style={{ color: c.color }} />
              {c.label}
            </div>
            <p className="font-display font-black text-xl text-[var(--lc-ink)] mt-1">{formatCOP(c.value)}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Chart data={data.monthly} title="Ventas y ganancias por mes" testid="fin-chart-monthly" />
        <Chart data={data.weekly} title="Ventas y ganancias por semana" testid="fin-chart-weekly" />
      </div>

      {/* Investments / Capital */}
      <div className="bg-white rounded-2xl border border-[var(--lc-border)] p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display font-extrabold text-xl text-[var(--lc-ink)] flex items-center gap-2">
            <Target className="w-5 h-5 text-[var(--lc-primary)]" /> Capital invertido
          </h3>
          <span className="font-display font-black text-xl text-[var(--lc-ink)]" data-testid="fin-capital">{formatCOP(data.capital)}</span>
        </div>
        <p className="text-sm text-[var(--lc-slate)] mb-4">Registra tus gastos: compra de inventario, plataforma, publicidad, etc.</p>

        <form onSubmit={addInvestment} className="flex flex-col sm:flex-row gap-2 mb-4">
          <input
            value={concept}
            onChange={(e) => setConcept(e.target.value)}
            placeholder="Concepto (ej: Publicidad, Inventario)"
            data-testid="investment-concept-input"
            className="flex-1 rounded-xl border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] outline-none px-4 py-2.5 bg-white"
          />
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            type="number"
            min="0"
            placeholder="Monto (COP)"
            data-testid="investment-amount-input"
            className="sm:w-44 rounded-xl border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] outline-none px-4 py-2.5 bg-white"
          />
          <button
            type="submit"
            disabled={adding}
            data-testid="add-investment-button"
            className="flex items-center justify-center gap-2 rounded-full bg-[var(--lc-primary)] text-white px-5 py-2.5 font-display font-extrabold transition-colors hover:bg-[var(--lc-primary-hover)] disabled:opacity-60"
          >
            {adding ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />} Agregar
          </button>
        </form>

        {data.investments.length === 0 ? (
          <p className="text-sm text-[var(--lc-slate)]">Aún no hay inversiones registradas.</p>
        ) : (
          <div className="space-y-2" data-testid="investments-list">
            {data.investments.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between rounded-xl bg-[var(--lc-bg)] px-4 py-2.5" data-testid={`investment-${inv.id}`}>
                <div className="min-w-0">
                  <span className="font-semibold text-[var(--lc-ink)] block">{inv.concept}</span>
                  {inv.created_at && (
                    <span className="text-xs text-[var(--lc-slate)]" data-testid={`investment-date-${inv.id}`}>
                      {new Date(inv.created_at).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-bold text-[var(--lc-ink)]">{formatCOP(inv.amount)}</span>
                  <button onClick={() => removeInvestment(inv.id)} data-testid={`delete-investment-${inv.id}`} className="text-[var(--lc-accent)] hover:scale-110 transition-transform" aria-label="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-book profit */}
      <div className="bg-white rounded-2xl border border-[var(--lc-border)] overflow-hidden">
        <h3 className="font-display font-extrabold text-xl text-[var(--lc-ink)] p-6 pb-3">Ganancia por libro</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="per-book-table">
            <thead>
              <tr className="bg-[var(--lc-bg)] text-[var(--lc-slate)] text-left">
                <th className="px-4 py-2 font-bold">Libro</th>
                <th className="px-4 py-2 font-bold text-right">Precio</th>
                <th className="px-4 py-2 font-bold text-right">Costo</th>
                <th className="px-4 py-2 font-bold text-right">Ganancia/u</th>
                <th className="px-4 py-2 font-bold text-right">Vendidos</th>
                <th className="px-4 py-2 font-bold text-right">Ganancia</th>
              </tr>
            </thead>
            <tbody>
              {data.per_book.map((b) => (
                <tr key={b.book_id} className="border-t border-[var(--lc-border)]" data-testid={`per-book-${b.book_id}`}>
                  <td className="px-4 py-2.5 text-[var(--lc-ink)] font-semibold">{b.title}</td>
                  <td className="px-4 py-2.5 text-right">{formatCOP(b.price)}</td>
                  <td className="px-4 py-2.5 text-right text-[var(--lc-slate)]">{formatCOP(b.cost)}</td>
                  <td className={`px-4 py-2.5 text-right font-bold ${b.profit_per_unit >= 0 ? "text-[var(--lc-success)]" : "text-[var(--lc-accent)]"}`}>
                    {formatCOP(b.profit_per_unit)}
                  </td>
                  <td className="px-4 py-2.5 text-right">{b.units_sold}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-[var(--lc-ink)]">{formatCOP(b.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
